import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import LazyLoading from '../../../components/LazyLoading';
import DefaultModal from '../../../components/default/DefaultModal';

import { useHistory } from 'react-router-dom';

import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';
import useTollRateService from '../../../app/services/hooks/useTollRateService';
import { TollRateDataForm, TollRateFilters } from '../../../app/models/TollRate';
import TollRateDataTable from '../components/tables/TollRateDataTable';
import TollRateCreate from '../components/TollRateCreate';
import TollRateEdit from '../components/TollRateEdit';
import { toast } from 'react-toastify';

interface Props {
    tollId: number;
}

const TollRateContainer = ({ tollId }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const history = useHistory();
    const { getAllTollRatesByToll, fetchingGetTollRatesByToll, deleteTollRate } =
        useTollRateService();

    const [showingEdit, setShowingEdit] = useState(false);

    const [showingCreate, setShowingCreate] = useState(false);

    const [tollRateIdEdit, setTollRateIdEdit] = useState<number>(-1);

    const [tollRates, setTollRates] = useState<TollRateDataForm[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [filter, setFilter] = useState<TollRateFilters>({
        toll_rate_id: null,
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (tollId: number) => {
        setShowingEdit(true);
        setTollRateIdEdit(tollId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setTollRateIdEdit(-1);
    };

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const destroy = (tollRateId: number) => {
        const _text = 'Está a punto de eliminar la tarifa de peaje #' + tollRateId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando tarifa de peaje...');
                        deleteTollRate(tollRateId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La tarifa de peaje no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const reloadTable = () => {
        getAllTollRatesByToll(tollId, {
            onSuccess: (response: ServiceResponse) => {
                setTollRates(response.data.toll_rates);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    return (
        <>
            <DefaultCard>
                {!fetchingGetTollRatesByToll ? (
                    <div className="row">
                        <div className="col-12 mb-2">
                            <ButtonCreate
                                title="Nueva tarifa de peaje"
                                callbackCreate={showCreate}
                            />
                        </div>
                        <div className="col-12">
                            {tollRates ? (
                                <TollRateDataTable
                                    tollRates={tollRates}
                                    edit={showEdit}
                                    destroy={destroy}
                                    loading={fetchingGetTollRatesByToll}
                                    totalRows={totalRows}
                                    filter={filter}
                                    setFilter={setFilter}
                                />
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <LazyLoading height="300" />
                )}
            </DefaultCard>
            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Tarifa de peaje"
                    backdrop={true}
                    showFooter={false}
                >
                    <TollRateCreate
                        tollId={tollId}
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Tarifa de peaje"
                    backdrop={true}
                    showFooter={false}
                >
                    <TollRateEdit
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                        tollRateId={tollRateIdEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default TollRateContainer;
